/**
 * WiCloud API Standard Response Types
 *
 * These types match the backend StandardResponse models exactly
 * to ensure seamless frontend-backend integration.
 *
 * IMPORTANT: All API endpoints MUST use these response formats.
 */

/**
 * Standard successful response with data payload
 * Used for GET, POST, PUT operations that return data
 */
export interface StandardResponse<T> {
  success: true;
  error: null;
  data: T;
}

/**
 * Message-only response (no data payload)
 * Used for operations that don't return data (delete, void operations)
 */
export interface MessageResponse {
  success: true;
  message: string;
}

/**
 * Response with both message and data
 * Used when operation needs to return data AND provide user feedback message
 */
export interface StandardResponseWithMessage<T> {
  success: true;
  message: string;
  data: T;
}

/**
 * Standard error response format
 */
export interface ErrorResponse {
  success: false;
  error: ErrorDetails;
}

/**
 * Error details structure
 */
export interface ErrorDetails {
  /**
   * Error code for programmatic handling
   * Examples: 'UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND', 'VALIDATION_ERROR', 'CONFLICT'
   */
  code: string;

  /**
   * Human-readable error message
   */
  message: string;

  /**
   * Additional error context (optional)
   * Can be structured data or array of validation errors
   */
  details?: unknown;
}

/**
 * Validation error detail for 422 responses
 */
export interface ValidationErrorDetail {
  field: string;
  reason: string;
}

/**
 * Paginated response format
 *
 * IMPORTANT: This is the STANDARDIZED pagination format.
 * All list endpoints return data in this structure.
 */
export interface PaginatedStandardResponse<T> {
  success: true;
  error: null;
  data: T[];
  pagination: PaginationInfo;
}

/**
 * Pagination information
 * IMPORTANT: This matches the backend PaginationInfo exactly
 * (WiCloud.Shared/API/Models/StandardResponse.cs)
 */
export interface PaginationInfo {
  /**
   * Current page number (1-based indexing)
   */
  currentPage: number;

  /**
   * Total number of pages
   */
  totalPages: number;

  /**
   * Number of items per page
   */
  itemsPerPage: number;

  /**
   * Total number of items across all pages
   */
  totalItems: number;

  /**
   * Whether there is a previous page
   */
  hasPrev: boolean;

  /**
   * Whether there is a next page
   */
  hasNext: boolean;
}

/**
 * Pagination query parameters (standardized)
 */
export interface PaginationParams {
  /**
   * Page number (1-based, default: 1)
   */
  page?: number;

  /**
   * Items per page (default: 20)
   */
  pageSize?: number;

  /**
   * Field to sort by (e.g., 'createdAt', 'name')
   */
  sortBy?: string;

  /**
   * Sort direction
   */
  sortOrder?: "asc" | "desc";
}

/**
 * Filter parameters extending pagination
 * Used for list endpoints with search/filter capabilities
 */
export interface FilterParams extends PaginationParams {
  /**
   * General search query
   */
  search?: string;

  /**
   * Additional filter fields (flexible)
   */
  [key: string]: string | number | boolean | undefined;
}

/**
 * Type guard to check if response is an error
 */
export function isErrorResponse(response: unknown): response is ErrorResponse {
  return (
    typeof response === "object" &&
    response !== null &&
    "success" in response &&
    response.success === false &&
    "error" in response
  );
}

/**
 * Type guard to check if response is successful
 */
export function isSuccessResponse<T>(
  response: unknown,
): response is StandardResponse<T> {
  return (
    typeof response === "object" &&
    response !== null &&
    "success" in response &&
    response.success === true &&
    "data" in response
  );
}

/**
 * Type guard to check if response is paginated
 */
export function isPaginatedResponse<T>(
  response: unknown,
): response is PaginatedStandardResponse<T> {
  return (
    isSuccessResponse(response) &&
    "pagination" in response &&
    Array.isArray((response as { data: unknown }).data)
  );
}

/**
 * Extract data from StandardResponse
 * Throws error if response is an error response
 */
export function extractData<T>(
  response: StandardResponse<T> | ErrorResponse,
): T {
  if (isErrorResponse(response)) {
    throw new Error(response.error.message);
  }
  return response.data;
}

/**
 * Extract data from PaginatedStandardResponse
 */
export function extractPaginatedData<T>(
  response: PaginatedStandardResponse<T> | ErrorResponse,
): { data: T[]; pagination: PaginationInfo } {
  if (isErrorResponse(response)) {
    throw new Error(response.error.message);
  }
  return {
    data: response.data,
    pagination: response.pagination,
  };
}
